import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Package, Star, Plus, MagnifyingGlass, Funnel, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductReviews() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productReviews, setProductReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchProducts = useCallback(async () => {
    try {
      let url = `${API}/products`;
      if (categoryFilter) url += `?category=${categoryFilter}`;
      
      const { data } = await axios.get(url);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchProductReviews = async (productId) => {
    try {
      const { data } = await axios.get(`${API}/product-reviews?product_id=${productId}`);
      setProductReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    fetchProductReviews(product.id);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    try {
      await axios.post(`${API}/product-reviews`, {
        product_id: selectedProduct.id,
        rating,
        review
      }, { withCredentials: true });
      
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setRating(5);
      setReview('');
      fetchProductReviews(selectedProduct.id);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStars = (rating, size = 20) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={size}
        weight={i < rating ? 'fill' : 'regular'}
        className={i < rating ? 'text-yellow-500' : 'text-gray-300'}
      />
    ));
  };

  const categories = ['insecticide', 'trap', 'equipment'];

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      
      {/* Hero */}
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Package size={48} weight="duotone" />
            <h1 className="text-5xl sm:text-6xl tracking-tight font-semibold">Product Reviews</h1>
          </div>
          <p className="text-xl leading-relaxed text-secondary max-w-4xl">
            Professional reviews of pest control products by industry experts. Find the best insecticides, traps, and equipment.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Search & Filters */}
        <div className="bg-white border border-borders rounded-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                data-testid="product-search"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="relative">
              <Funnel size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                data-testid="category-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
              >
                <option value="">All Categories</option>
                <option value="insecticide">Insecticides</option>
                <option value="trap">Traps</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product List */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl tracking-tight font-medium mb-6">Products ({filteredProducts.length})</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white border border-borders rounded-sm p-6 text-center">
                <p className="text-gray-600">No products found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProducts.map(product => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    data-testid={`product-${product.id}`}
                    className={`w-full text-left bg-white border rounded-sm p-4 transition-all hover:-translate-y-1 ${
                      selectedProduct?.id === product.id ? 'border-accent shadow-md' : 'border-borders'
                    }`}
                  >
                    <h3 className="text-lg font-medium mb-2">{product.name}</h3>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-gray-100 text-xs rounded-sm uppercase tracking-[0.1em]">
                        {product.category}
                      </span>
                      {product.average_rating && (
                        <div className="flex items-center gap-1">
                          <Star size={16} weight="fill" className="text-yellow-500" />
                          <span className="text-sm font-medium">{product.average_rating.toFixed(1)}</span>
                          <span className="text-xs text-gray-500">({product.review_count})</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Detail & Reviews */}
          <div className="lg:col-span-2">
            {selectedProduct ? (
              <div>
                <div className="bg-white border border-borders rounded-sm p-8 mb-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-3xl tracking-tight font-medium mb-2">{selectedProduct.name}</h2>
                      <span className="px-3 py-1 bg-gray-100 text-sm rounded-sm uppercase tracking-[0.1em]">
                        {selectedProduct.category}
                      </span>
                    </div>
                    {selectedProduct.average_rating && (
                      <div className="text-right">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex gap-1">
                            {renderStars(Math.round(selectedProduct.average_rating), 24)}
                          </div>
                        </div>
                        <p className="text-2xl font-semibold">{selectedProduct.average_rating.toFixed(1)}</p>
                        <p className="text-sm text-gray-600">{selectedProduct.review_count} reviews</p>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-base leading-relaxed mb-6 text-gray-700">{selectedProduct.description}</p>
                  
                  {selectedProduct.video_url && (
                    <div className="mb-6">
                      <h3 className="text-xl font-medium mb-3">Product Review Video</h3>
                      <div className="aspect-video bg-gray-100 rounded-sm overflow-hidden">
                        <iframe
                          width="100%"
                          height="100%"
                          src={selectedProduct.video_url.replace('watch?v=', 'embed/')}
                          title="Product Review Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        ></iframe>
                      </div>
                    </div>
                  )}
                  
                  {selectedProduct.affiliate_link && (
                    <div className="mb-6">
                      <a
                        href={selectedProduct.affiliate_link}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3 rounded-sm hover:bg-accent/90 transition-colors"
                        data-testid="buy-product-link"
                      >
                        Buy This Product
                        <ArrowRight size={20} />
                      </a>
                      <p className="text-xs text-gray-500 mt-2">* Affiliate link - We may earn a commission</p>
                    </div>
                  )}
                  
                  {user && !showReviewForm && (
                    <button
                      onClick={() => setShowReviewForm(true)}
                      data-testid="write-review-btn"
                      className="flex items-center gap-2 bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                    >
                      <Plus size={20} />
                      Write a Review
                    </button>
                  )}

                  {!user && (
                    <p className="text-gray-600 italic">Please login to write a review</p>
                  )}
                </div>

                {/* Review Form */}
                {showReviewForm && (
                  <div className="bg-white border border-borders rounded-sm p-8 mb-8">
                    <h3 className="text-2xl tracking-tight font-medium mb-6">Write Your Review</h3>
                    <form onSubmit={handleSubmitReview} className="space-y-6">
                      <div>
                        <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Rating</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setRating(star)}
                              className="hover:scale-110 transition-transform"
                              data-testid={`rating-${star}`}
                            >
                              <Star
                                size={32}
                                weight={star <= rating ? 'fill' : 'regular'}
                                className={star <= rating ? 'text-yellow-500' : 'text-gray-300'}
                              />
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Your Review</label>
                        <textarea
                          data-testid="review-text"
                          value={review}
                          onChange={(e) => setReview(e.target.value)}
                          required
                          rows="6"
                          className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Share your professional experience with this product..."
                        ></textarea>
                      </div>

                      <div className="flex gap-4">
                        <button
                          type="submit"
                          data-testid="submit-product-review"
                          className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                        >
                          Submit Review
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowReviewForm(false)}
                          className="bg-gray-200 text-text_dark px-6 py-3 rounded-sm hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Reviews List */}
                <div>
                  <h3 className="text-2xl tracking-tight font-medium mb-6">
                    Customer Reviews ({productReviews.length})
                  </h3>
                  
                  {productReviews.length === 0 ? (
                    <div className="bg-white border border-borders rounded-sm p-8 text-center">
                      <p className="text-gray-600">No reviews yet. Be the first to review this product!</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {productReviews.map(review => (
                        <div key={review.id} className="bg-white border border-borders rounded-sm p-6" data-testid={`review-${review.id}`}>
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <p className="font-medium text-lg">{review.user_name}</p>
                              <p className="text-sm text-gray-600">
                                {new Date(review.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              {renderStars(review.rating)}
                            </div>
                          </div>
                          <p className="text-base leading-relaxed text-gray-700">{review.review}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-borders rounded-sm p-12 text-center">
                <Package size={64} className="mx-auto mb-4 text-gray-400" weight="duotone" />
                <p className="text-xl text-gray-600">Select a product to view reviews and details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
